import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import debounce from 'lodash.debounce';
import classNames from 'classnames';
import { Typography } from '@hyphengroup/cag-components';
import Icon from './Icon';
import Spinner from './Spinner';
import OutsideClickHandler from './OutsideClickHandler';
import DialogBox from './DialogBox';
import Option from './Option';

import './Button.scss';

const OptionPanel = (props) => {
  const {
    options = [],
    onClickOptionWithIndex,
    onClickOutsideOptionPanel,
    dataSelectorId,
  } = props;

  return (
    <OutsideClickHandler
      onOutsideClick={onClickOutsideOptionPanel}
    >
      <DialogBox
        className="options"
        fluid
      >
        {options && options
          .map(({ label, value }, i) => (
            <Option
              value={value}
              key={label + value}
              onClick={() => {
                onClickOptionWithIndex(i);
              }}
              dataSelectorId={dataSelectorId}
            >
              {label || value}
            </Option>
          ))}
      </DialogBox>
    </OutsideClickHandler>
  );
};

OptionPanel.defaultProps = {
  options: [],
  dataSelectorId: null,
};

OptionPanel.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  })),
  onClickOptionWithIndex: PropTypes.func.isRequired,
  onClickOutsideOptionPanel: PropTypes.func.isRequired,
  dataSelectorId: PropTypes.string,
};

class Button extends React.Component {
  constructor() {
    super();
    this.state = {
      isOptionPanelShow: false,
      selectedIndex: null,
      entered: false,
    };

    this.buttonRef = React.createRef();
    this.takeFocus = this.takeFocus.bind(this);
    this.onClickSelectButton = this.onClickSelectButton.bind(this);
    this.onClickOutsideOptionPanel = this.onClickOutsideOptionPanel.bind(this);
    this.toggleOptionPanel = this.toggleOptionPanel.bind(this);
    this.debounceToggleOptionPanel = debounce(this.toggleOptionPanel, 50).bind(this);
    this.onClickOptionWithIndex = this.onClickOptionWithIndex.bind(this);
  }

  static getDerivedStateFromProps(nextProps) {
    const { options, value } = nextProps;
    if (value) {
      const index = options.findIndex(x => _.isEqual(x.value, value));
      return {
        selectedIndex: index >= 0 ? index : null,
      };
    }

    return null;
  }

  onClickSelectButton() {
    this.debounceToggleOptionPanel();
  }

  onClickOutsideOptionPanel() {
    this.debounceToggleOptionPanel();
  }

  onClickOptionWithIndex(i) {
    const { options, onChange } = this.props;
    this.setState({
      selectedIndex: i,
      isOptionPanelShow: false,
    });
    if (onChange) {
      onChange(options[i]);
    }
  }

  takeFocus() {
    this.buttonRef.current.focus();
    // On first entry, drop it down.
    const { selectedIndex, entered } = this.state;
    if (selectedIndex == null && !entered) {
      this.setState({ entered: true });
      this.debounceToggleOptionPanel();
    }
  }

  toggleOptionPanel() {
    const { isOptionPanelShow } = this.state;
    this.setState({
      isOptionPanelShow: !isOptionPanelShow,
    });
  }

  render() {
    const {
      isOptionPanelShow,
      selectedIndex,
    } = this.state;

    const {
      className,
      noMinWidth,
      onClick,
      onMouseDown,
      massive,
      xLarge,
      large,
      small,
      primary,
      secondary,
      tertiary,
      alert,
      styleName,
      loading,
      disabled,
      fluid,
      children,
      as,
      topIconName,
      leftIconName,
      rightIconName,
      text,
      fontStyle,
      fontSize,
      fontWeight,
      dark,
      options = [],
      inline,
      placeholder,
      dataSelectorId,
      dataSelectorValue,
    } = this.props;
    const sharedClasses = {
      'no-min-width': noMinWidth,
      massive,
      'x-large': xLarge,
      large,
      small,
      primary,
      secondary,
      tertiary,
      alert,
      [styleName]: true,
      dark,
      disabled,
      fluid,
      loading,
    };

    const classes = {
      button: !text && !inline,
      'text-button': as === 'button' && text,
      'select-layout': as === 'select',
      'inline-select': as === 'select' && inline,
      ...sharedClasses,
      [className]: !_.isEmpty(className),
    };

    const buttonSelectWrapperClasses = {
      'button-select-wrapper': true,
      ...sharedClasses,
    };

    switch (as) {
      case 'select':
        return (
          <span className={classNames(buttonSelectWrapperClasses)}>
            <button
              type="button"
              className={classNames(classes)}
              ref={this.buttonRef}
              data-selector={dataSelectorId}
              data-selector-value={_.get(options, `[${selectedIndex}].value`)}
              data-selector-disabled={disabled}
              data-selector-loading={loading}
              onClick={!(disabled || loading) ? this.onClickSelectButton : undefined}
              data-selector-type="button-select"
            >
              <div className="left-right-icon-wrapper">
                {selectedIndex != null
                  ? (
                    <Typography variant={fontStyle} sizeFont={fontSize} weight={fontWeight}>
                      {_.get(options, `[${selectedIndex}].label`)
                      || _.get(options, `[${selectedIndex}].value`)}
                    </Typography>
                  )
                  : (
                    <div className="placeholder">
                      {placeholder}
                    </div>
                  )
                }
                <div className="right-icon">
                  <Icon name={inline ? 'arrow_down' : 'chevron_down'} />
                </div>
              </div>
            </button>
            {isOptionPanelShow && (
              <OptionPanel
                options={options}
                onClickOptionWithIndex={this.onClickOptionWithIndex}
                onClickOutsideOptionPanel={this.onClickOutsideOptionPanel}
                dataSelectorId={dataSelectorId}
              />
            )}
          </span>
        );
      case 'button':
      default:
        return (
          <button
            type="button"
            onMouseDown={!(disabled || loading) ? onMouseDown : undefined}
            onClick={!(disabled || loading) ? onClick : undefined}
            className={classNames(classes)}
            data-selector={dataSelectorId}
            data-selector-value={dataSelectorValue}
            data-selector-disabled={disabled}
            data-selector-loading={loading}
            data-selector-type="button"
          >
            {topIconName && (
              <div className="top-icon">
                <Icon name={topIconName} medium />
              </div>
            )}
            <div className="left-right-icon-wrapper">
              {leftIconName && (
                <div className="left-icon">
                  <Icon name={leftIconName} />
                </div>
              )}
              <div>{children}</div>
              {rightIconName && (
                <div className="right-icon">
                  <Icon name={rightIconName} />
                </div>
              )}
            </div>
            {loading && (
              <div className="button-spinner-wrapper">
                <Spinner />
              </div>
            )}
          </button>
        );
    }
  }
}

Button.defaultProps = {
  className: null,
  noMinWidth: false,
  as: 'button',
  massive: false,
  xLarge: false,
  large: false,
  small: false,
  primary: false,
  secondary: false,
  tertiary: false,
  alert: false,
  styleName: 'normal',
  disabled: false,
  onClick: undefined,
  fluid: false,
  leftIconName: null,
  rightIconName: null,
  topIconName: null,
  loading: false,
  text: false,
  fontStyle: 'primary',
  fontSize: 'px14',
  fontWeight: 'normal',
  dark: false,
  onChange: undefined,
  onMouseDown: undefined,
  placeholder: null,
  options: [],
  value: null,
  inline: false,
  children: null,
  dataSelectorId: null,
  dataSelectorValue: null,
};

Button.propTypes = {
  className: PropTypes.string,

  /** Remove min width */
  noMinWidth: PropTypes.bool,

  /** Render as "button" or "select" element */
  as: PropTypes.oneOf(['button', 'select']),

  /** Size massive */
  massive: PropTypes.bool,

  /** Size extra large */
  xLarge: PropTypes.bool,

  /** Size large */
  large: PropTypes.bool,

  /** Size small */
  small: PropTypes.bool,

  /** Level Primary */
  primary: PropTypes.bool,

  /** Level Secondary */
  secondary: PropTypes.bool,

  /** Level Tertiary */
  tertiary: PropTypes.bool,

  /** Level Alert */
  alert: PropTypes.bool,

  /** Style "Normal" or "Ghost" */
  styleName: PropTypes.oneOf(['normal', 'ghost']),

  /** State disabled */
  disabled: PropTypes.bool,

  /** Width depends on container */
  fluid: PropTypes.bool,

  /** Click event, "button" only */
  onClick: PropTypes.func,

  /** Mouse down event, "button" only */
  onMouseDown: PropTypes.func,

  /** Left icon, "button" only */
  leftIconName: PropTypes.string,

  /** Right icon, "button" only */
  rightIconName: PropTypes.string,

  /** Top icon, "button" only */
  topIconName: PropTypes.string,

  /** State loading, "button" only  */
  loading: PropTypes.bool,

  /** Text Button, "button" only, only take "onClick", "leftIconName",
      "rightIconName", "dark", "loading" & "disabled" props */
  text: PropTypes.bool,

  /** Display font style props */
  fontStyle: PropTypes.string,

  /** Display font size props */
  fontSize: PropTypes.string,

  /** Display font weight props */
  fontWeight: PropTypes.string,

  /** Text Button style dark */
  dark: PropTypes.bool,

  /** Change event, "select" only */
  onChange: PropTypes.func,

  /** Placeholder text, "select" only */
  placeholder: PropTypes.string,

  /** Select Options, "select" only */
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,

  })),

  /** Select default value, "select" only */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),

  /** inline select, "select" only, only take "onChange",
      "small", "large", "options", "fluid", "disabled" props */
  inline: PropTypes.bool,

  children: PropTypes.node,

  /** Used by CAG Automation Team only */
  dataSelectorId: PropTypes.string,
  dataSelectorValue: PropTypes.string,
};

export default Button;
